import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DocumentInfo } from '../models/domain/DocumentInfo';
import {
    FilePreviewModalComponent,
    FilePreviewModalData
} from '../components/file-preview-modal/file-preview-modal.component';

/** Extensions that we can definitely preview in the browser */
const PREVIEWABLE_IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'svg']);

@Injectable({
    providedIn: 'root'
})
export class FilePreviewService {
    previewUrlCache: Map<string, string> = new Map();

    constructor(private readonly dialog: MatDialog) { }

    /** Returns true if the file format supports in-browser preview */
    canPreview(fileName: string): boolean {
        if (!fileName) return false;
        const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
        return ext === 'pdf' || PREVIEWABLE_IMAGE_EXTENSIONS.has(ext);
    }

    /**
     * Opens the preview modal.
     * @param info File identification (ID and Name)
     * @param existingUrl Optional object URL if already fetched (e.g. thumbnails)
     */
    public openPreview(info: Partial<DocumentInfo> | { FileId: string; FileName: string }, existingUrl?: string): void {
        this.openFilePreviewDialog(info, existingUrl);
    }

    public openCachedPreview(
        info: Partial<DocumentInfo> | { FileId: string; FileName: string },
        useCache: boolean
    ) {
        if (info?.FileId && this.previewUrlCache.has(info?.FileId)) {
            this.openFilePreviewDialog(info, this.previewUrlCache.get(info?.FileId));
        } else {
            this.openFilePreviewDialog(info, null, useCache);
        }
    }

    private openFilePreviewDialog(
        info: Partial<DocumentInfo> | { FileId: string; FileName: string },
        existingUrl?: string,
        useCache: boolean = false
    ) {
        const fileId = info.FileId;
        const fileName = info.FileName;

        if (!fileId || !fileName || !this.canPreview(fileName)) {
            return;
        }

        const data: FilePreviewModalData = {
            fileName: fileName,
            fileId: fileId,
            previewUrl: existingUrl ?? null,
            isPdf: fileName.toLowerCase().endsWith('.pdf'),
            shouldCache: useCache
        };

        this.dialog.open(FilePreviewModalComponent, {
            data,
            panelClass: 'file-preview-dialog'
        });
    }

    public addToCache(fileId: string, previewUrl: string): void {
        this.previewUrlCache.set(fileId, previewUrl);
    }

    public clearCachedPreviewUrls(): void {
        this.previewUrlCache.forEach((previewUrl, _) => {
            URL.revokeObjectURL(previewUrl);
        });

        this.previewUrlCache.clear();
    }
}
